import { useState } from "react";
import { Mail, Phone, ShieldCheck, UserPlus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { fetchJson } from "@/lib/api";

import { AuthorizedIndividualForm } from "./components/AuthorizedIndividualForm";
import { CountdownChip } from "./components/CountdownChip";
import { IdUploader } from "./components/IdUploader";
import { daysUntil, formatDate } from "./format";

// Phase 4c id-capture FR-5 / AC-10. Customer-portal IDs page. The
// page is intentionally redaction-strict: it never renders the ID
// image bytes (admin/dispatch-only, FR-3) and never the legal_name —
// only the kind, last4, expiry, and status.
//
// Phase 4n.1 portal empty-state polish already replaced the
// list-empty copy and hid the internal "self" relationship row.
// This file (Phase 4q portal-ids-polish) layers in:
//
//   - Custom drag-drop IdUploader (replaces the native file picker
//     that broke the design system, called out in
//     .notes/design-grades/portal/05-ids.md issue #2).
//   - The customer's own "self" row is surfaced as a distinct "You"
//     panel at the top instead of being hidden silently — gives the
//     customer a visible anchor for their own IDs.
//   - Expiry dates carry a CountdownChip ("expires in 60 days",
//     amber within 30 days, red overdue) — only shown when expiry
//     is within 180 days, since "expires in 1461 days" reads as
//     noise (issue #8).
//   - Human-readable formatted dates via `formatDate` (issue #8).
//   - Phone + email surfaced inline on operator rows so dispatch
//     handoff doesn't require drilling in.

interface IdentificationSummary {
  id: number;
  id_kind: string;
  id_kind_label: string;
  issuing_jurisdiction: string | null;
  last4: string | null;
  expires_on: string | null;
  status: "pending_review" | "accepted" | "rejected" | "expired";
  rejected_reason: string | null;
}

interface AuthorizedIndividualSummary {
  id: number;
  name: string;
  relationship: string;
  phone_e164: string | null;
  email: string | null;
  accepted_ids_count: number;
}

interface IdsListPayload {
  identifications: IdentificationSummary[];
  authorized_individuals: AuthorizedIndividualSummary[];
}

// AuthorizedIndividual::RELATIONSHIPS — matches
// app/models/authorized_individual.rb. "self" is filtered out before
// this helper is called.
const RELATIONSHIP_LABELS: Readonly<Record<string, string>> = {
  employee: "Employee",
  family: "Family",
  subcontractor: "Subcontractor",
  other: "Other"
};

function humaniseRelationship(value: string): string {
  return RELATIONSHIP_LABELS[value] ?? value.replace(/_/g, " ");
}

export function IdsPage(): JSX.Element {
  const list = useQuery({
    queryKey: ["portal", "ids"],
    queryFn: () => fetchJson<IdsListPayload>("/portal/account/identifications.json")
  });

  return (
    <section className="flex flex-col gap-6">
      <header className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">Account</p>
        <h1 className="font-serif text-2xl font-bold italic tracking-tight text-brand-black md:text-3xl">
          My IDs
        </h1>
        <span aria-hidden="true" className="inline-block h-0.5 w-10 bg-brand-red" />
        <p className="pt-2 text-sm text-zinc-600">
          Government-issued IDs we keep on file so we can release equipment to you on
          delivery. We never share images with anyone outside of JDR dispatch.
        </p>
      </header>

      {list.isPending ? <p className="text-sm text-zinc-500">Loading…</p> : null}
      {list.isError ? (
        <p className="text-sm text-brand-red">Could not load your IDs. Try again.</p>
      ) : null}

      {list.data ? (
        <IdsContent
          identifications={list.data.identifications}
          authorized_individuals={list.data.authorized_individuals}
        />
      ) : null}
    </section>
  );
}

function IdsContent({
  identifications,
  authorized_individuals
}: IdsListPayload): JSX.Element {
  const self = authorized_individuals.find((ai) => ai.relationship === "self");
  const others = authorized_individuals.filter((ai) => ai.relationship !== "self");

  return (
    <>
      <YouPanel self={self} idCount={identifications.length} />
      <IdList rows={identifications} />
      <IdUploader />
      <AuthorizedIndividualsList rows={others} />
    </>
  );
}

function YouPanel({
  self,
  idCount
}: {
  self: AuthorizedIndividualSummary | undefined;
  idCount: number;
}): JSX.Element {
  // The `self` row is created by the CustomerProfile callback at
  // signup — it's how the server links uploaded IDs back to the
  // customer. Phase 4n.1 hid the row entirely; we surface it here
  // as a "You" panel so the customer has a visible anchor for their
  // own IDs without the "self" enum leaking.
  return (
    <div className="rounded-lg border border-flag-navy/20 bg-flag-navy/5 p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-flag-navy/10 p-2 text-flag-navy">
          <ShieldCheck aria-hidden="true" className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-brand-black">
            {self?.name ?? "You"}
          </p>
          <p className="mt-0.5 text-xs text-zinc-600">
            {idCount === 0
              ? "Add at least one ID below so we can verify you at delivery."
              : `${idCount} ID${idCount === 1 ? "" : "s"} on file.`}
          </p>
        </div>
      </div>
    </div>
  );
}

function IdList({ rows }: { rows: IdentificationSummary[] }): JSX.Element {
  if (rows.length === 0) {
    // Phase 4n.1 empty-state copy — the customer needs to know what
    // to upload and *why* (FR-3 compliance gating equipment release).
    return (
      <div
        className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-5 text-center"
        data-testid="ids-empty"
      >
        <p className="text-sm font-medium text-brand-black">No IDs on file yet</p>
        <p className="mt-1 text-sm text-zinc-600">
          We verify a government-issued ID before we release equipment.
          Upload one below — it&rsquo;s reviewed in under a business day.
        </p>
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2" aria-label="Identifications on file">
      {rows.map((row) => (
        <li
          key={row.id}
          data-testid={`id-row-${row.id}`}
          className="rounded-lg border border-zinc-200 bg-white p-3"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-brand-black">
                {row.id_kind_label}
                {row.issuing_jurisdiction ? ` · ${row.issuing_jurisdiction}` : ""}
              </p>
              <p className="mt-0.5 text-xs text-zinc-500">
                {row.last4 ? `········${row.last4}` : "no last-4 on file"}
              </p>
              {row.expires_on ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-zinc-600">
                  <span>Expires {formatDate(row.expires_on)}</span>
                  <ExpiryChip iso={row.expires_on} />
                </div>
              ) : null}
              {row.rejected_reason ? (
                <p className="mt-1.5 text-xs text-brand-red">Reason: {row.rejected_reason}</p>
              ) : null}
            </div>
            <StatusBadge status={row.status} />
          </div>
        </li>
      ))}
    </ul>
  );
}

// Phase 4q portal-ids-polish — only show the CountdownChip for IDs
// that are close enough to expiry that the customer should act soon
// (≤ 180 days) or already overdue. For IDs valid years out the
// countdown reads as noise ("Expires in 1461 days"); the plain
// formatted date is enough on its own.
function ExpiryChip({ iso }: { iso: string }): JSX.Element | null {
  const days = daysUntil(iso);
  if (days == null || days > 180) return null;
  return <CountdownChip label="Expires" days={days} />;
}

function StatusBadge({ status }: { status: IdentificationSummary["status"] }): JSX.Element {
  const map: Record<IdentificationSummary["status"], { label: string; cls: string }> = {
    pending_review: { label: "Pending review",   cls: "bg-amber-100 text-amber-800" },
    accepted:       { label: "Accepted",         cls: "bg-emerald-100 text-emerald-800" },
    rejected:       { label: "Re-upload needed", cls: "bg-red-100 text-red-800" },
    expired:        { label: "Expired",          cls: "bg-zinc-200 text-zinc-700" }
  };
  const { label, cls } = map[status];
  return (
    <span data-testid="id-status" className={`shrink-0 rounded-full px-3 py-1 text-xs ${cls}`}>
      {label}
    </span>
  );
}

function AuthorizedIndividualsList({
  rows
}: {
  rows: AuthorizedIndividualSummary[];
}): JSX.Element {
  const [showForm, setShowForm] = useState(false);

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-brand-black">Authorized individuals</h2>
        <p className="text-xs text-zinc-500">
          Coworkers, family members, or anyone else who may sign for or operate
          equipment on your behalf.
        </p>
      </div>

      {rows.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-5 text-center"
          data-testid="authorized-individuals-empty"
        >
          <p className="text-sm font-medium text-brand-black">
            No additional operators on file
          </p>
          <p className="mt-1 text-sm text-zinc-600">
            Add someone if a coworker, spouse, or family member will be
            operating equipment for this account.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((ai) => (
            <li
              key={ai.id}
              data-testid={`individual-row-${ai.id}`}
              className="rounded-lg border border-zinc-200 bg-white p-3 text-sm text-brand-black"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{ai.name}</p>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {humaniseRelationship(ai.relationship)} ·{" "}
                    {ai.accepted_ids_count > 0
                      ? `${ai.accepted_ids_count} ID on file`
                      : "no ID yet"}
                  </p>
                  {(ai.phone_e164 || ai.email) ? (
                    <p className="mt-1 flex flex-wrap items-center gap-3 text-xs text-zinc-600">
                      {ai.phone_e164 ? (
                        <span className="inline-flex items-center gap-1">
                          <Phone aria-hidden="true" className="h-3 w-3" />
                          {ai.phone_e164}
                        </span>
                      ) : null}
                      {ai.email ? (
                        <span className="inline-flex items-center gap-1">
                          <Mail aria-hidden="true" className="h-3 w-3" />
                          {ai.email}
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm ? (
        <AuthorizedIndividualForm onClose={() => setShowForm(false)} />
      ) : (
        <Button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="add-authorized-individual"
          className="w-fit min-h-[44px]"
        >
          <UserPlus aria-hidden="true" className="mr-1.5 h-4 w-4" />
          Add authorized individual
        </Button>
      )}
    </section>
  );
}
